import React, { useMemo, useState } from 'react';
import { SenseHint } from './SenseChooser.js';
import { Button, Flex, Tag, Typography } from 'antd';
import { SoundOutlined } from '@ant-design/icons';
import { TranslationLanguage, displayIpa } from '@lingoping/core';
import { speakText } from '../utils/audio.js';
import {
  deckWordArticle,
  deckWordConjugation,
  deckWordMeaning,
  deckWordPalette,
  deckWordLemma,
} from '../utils/deckWords.js';
import type { CloudDeckWord } from '../services/cloudDecks.js';
import { ConjugationTabs } from './ConjugationTabs.js';
import { GenderBadge } from './GenderBadge.js';
import { TranslationText } from './TranslationText.js';

const { Text, Title } = Typography;

interface Props {
  word: CloudDeckWord;
  translationLanguage: TranslationLanguage;
}

/**
 * One deck word on a swipe card: article, word, pronunciation, plural or verb forms, the meaning
 * in the learner's language and an example, when the word has been prepared. Words that have not
 * been prepared yet show the word with its context sentence and any translation from the file.
 */
export const DeckWordFace: React.FC<Props> = ({ word, translationLanguage }) => {
  const [showTenses, setShowTenses] = useState(false);
  const library = word.word;
  const palette = deckWordPalette(word);
  const article = deckWordArticle(word);
  const table = useMemo(() => deckWordConjugation(word), [word]);
  const meaning = deckWordMeaning(word, translationLanguage);
  const plural = library?.linguistic_meta?.plural;
  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const say = (text: string) => speakText(text, 'de');

  return (
    <Flex vertical align="center" gap={6} style={{ textAlign: 'center', width: '100%' }}>
      {library?.part_of_speech && (
        <Tag style={{ margin: 0, textTransform: 'capitalize' }}>{library.part_of_speech}</Tag>
      )}
      {article && <GenderBadge article={article} palette={palette} size="large" />}
      <Title level={2} style={{ margin: '4px 0 0' }}>
        {deckWordLemma(word)}
        <SenseHint hint={word.sense_hint ?? library?.sense_hint} style={{ fontSize: 18 }} />
      </Title>
      <Text type="secondary">
        {library?.phonetic_ipa ? `${displayIpa(library.phonetic_ipa)} ` : ''}
        <Button
          type="text"
          size="small"
          icon={<SoundOutlined />}
          onPointerDown={stop}
          onClick={() => say(article ? `${article} ${deckWordLemma(word)}` : deckWordLemma(word))}
          aria-label="Pronounce word"
        />
      </Text>

      {plural && (
        <div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            Plural
          </Text>
          <div style={{ fontSize: 18, fontWeight: 600, color: palette.color }}>{plural}</div>
          <Button
            type="text"
            size="small"
            icon={<SoundOutlined />}
            onPointerDown={stop}
            onClick={() => say(plural)}
            aria-label="Pronounce plural"
          />
        </div>
      )}

      {table && (
        <div style={{ width: '100%' }} onPointerDown={stop}>
          <Text style={{ fontSize: 13 }}>{table.principal_parts}</Text>
          <div>
            <Button type="link" size="small" onClick={() => setShowTenses((v) => !v)}>
              {showTenses ? 'Hide tenses' : 'See all tenses'}
            </Button>
          </div>
          {showTenses && <ConjugationTabs table={table} />}
        </div>
      )}

      {meaning ? (
        <TranslationText
          as="h4"
          text={meaning.text}
          lang={meaning.textLang}
          style={{ margin: '8px 0 0', fontSize: 20, fontWeight: 600, color: 'rgba(0, 0, 0, 0.88)' }}
        />
      ) : word.sense_label ? (
        // One meaning of a word with several, before its card text exists.
        <Text style={{ fontSize: 18, marginTop: 8 }}>{word.sense_label}</Text>
      ) : word.hint ? (
        <Text style={{ fontSize: 18, marginTop: 8 }}>
          {word.hint}{' '}
          <Text type="secondary" style={{ fontSize: 11 }}>
            (from your file)
          </Text>
        </Text>
      ) : (
        <Text type="secondary" style={{ fontSize: 13, marginTop: 8 }}>
          The meaning shows once you choose Learn it.
        </Text>
      )}

      {library?.example_target && (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {library.example_target}{' '}
          <Button
            type="text"
            size="small"
            icon={<SoundOutlined />}
            onPointerDown={stop}
            onClick={() => say(library.example_target as string)}
            aria-label="Pronounce example sentence"
          />
        </Text>
      )}
      {word.context && (
        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic', marginTop: 4 }}>
          “{word.context}”
        </Text>
      )}
    </Flex>
  );
};
